import format from 'string-format';
import React from 'react';
import PropTypes from 'prop-types';
import _path from 'ramda/src/path';
import reMLSData from './MlsList';
import { RESX_LISTINGS } from '../../Constants';
import resx from '../../utilities/resx';

const ListingLabels = ({ listing, isOwner, handleLabelOpenMenuClick, expiryLabelRef }) => {
    const {
        HideAddress,
        IsActive,
        IsLive,
        IsSold,
        ListingPushedToMLS,
        ListingTransactionType,
        MarketStatus,
        MLSGroupType,
        MLSPremarketIntervalHours,
        PushToZillow,
        WithdrawnRemainingDays,
        HoursToExpiry
    } = listing;

    const listingExpiryNotificationInDays = _path(['Environment', 'ListingExpiryNotificationInHours'], RESAAS) / 24;
    const gateAutoExpireListingsFeature = _path(['Environment', 'GateAutoExpireListingsFeature'], RESAAS);
    const renderOffmarketLabel = marketStatus => {
        if (marketStatus === 2) {
            return (
                <div className="re-post-label re-post-label-premarket" data-testid="offmarket">
                    <span>{resx(RESX_LISTINGS, 'OFFMARKET_LABEL')}</span>
                </div>
            );
        }
        return null;
    };

    const renderPremarketLabel = (marketStatus, mlsGroupType, isSold, isOwner) => {
        // mls premarket: if you are not the owner and the listng is premarket and is MLS
        if (!isOwner && marketStatus === 1 && !isSold && reMLSData.read(mlsGroupType)) {
            return (
                <div className="re-post-label mls-premarket-featured">
                    <span className="re-icon re-icon-xs re-icon-lock">
                        {resx(RESX_LISTINGS, `PREMARKET_LABEL_${reMLSData.read(mlsGroupType).string.toUpperCase()}`)}
                    </span>
                </div>
            );
            // public premarket: if you are the owner and it is pre-market and it is not MLS
        } else if (marketStatus === 1 && !reMLSData.read(mlsGroupType)) {
            return (
                <div className="re-post-label re-post-label-premarket" data-testid="publicpremarketlabel">
                    <span>{resx(RESX_LISTINGS, 'PUBLIC_PRE_MARKET_LISTING_LABEL')}</span>
                </div>
            );
        }

        return null;
    };

    const renderPushedToMlsLabel = (isOwner, listingPushedToMLS, mLSGroupType) => {
        if (isOwner && listingPushedToMLS && mLSGroupType && reMLSData.read(mLSGroupType)) {
            const mlsPushedLabel = `${reMLSData.read(mLSGroupType).string.toUpperCase()}_MLS_LISTING_PUSHED`;
            const mlsPushedResource = resx(RESX_LISTINGS, mlsPushedLabel);
            return (
                <div className="re-post-label mls-uploaded-label">
                    <span className="re-icon re-icon-xs re-icon-upload">{mlsPushedResource}</span>
                </div>
            );
        }
        return null;
    };

    const renderWithdrawnTimeLabel = (isOwner, isSold, isActive, mLSPremarketIntervalHours) => {
        let mLSPremarketTime;
        let mLSPremarketTimeText;

        // Show 'days left before withdrawn' label for premarket listings
        if (!isSold && isOwner && isActive && mLSPremarketIntervalHours) {
            if (mLSPremarketIntervalHours > 48) {
                mLSPremarketTime = Math.round(mLSPremarketIntervalHours / 24);
                mLSPremarketTimeText = format(
                    resx(RESX_LISTINGS, 'LISTINGS_LABEL_MLS_PREMARKET_TIME_DAYS_TEXT'),
                    mLSPremarketTime
                );
            } else {
                mLSPremarketTime = mLSPremarketIntervalHours;
                if (mLSPremarketTime > 1) {
                    mLSPremarketTimeText = format(
                        resx(RESX_LISTINGS, 'LISTINGS_LABEL_MLS_PREMARKET_TIME_HOURS_TEXT'),
                        mLSPremarketTime
                    );
                } else {
                    mLSPremarketTimeText = format(
                        resx(RESX_LISTINGS, 'LISTINGS_LABEL_MLS_PREMARKET_TIME_HOUR_TEXT'),
                        mLSPremarketTime
                    );
                }
            }
            return (
                <div className="re-post-label premarket-days-label">
                    <span className="re-icon re-icon-xs re-icon-lock">{mLSPremarketTimeText}</span>
                </div>
            );
        }
        return null;
    };

    // for MLS pre-market listing
    const renderTemporarilyWithdrawnLabel = (isOwner, isActive, marketStatus, mLSGroupType) => {
        if (isOwner && !isActive && mLSGroupType) {
            return (
                <div className="re-post-label temporarily-withdrawn-label" data-testid="temporarilywithdrawnlabel">
                    {resx(RESX_LISTINGS, 'TEMPORARILY_WITHDRAWN_TEXT')}
                </div>
            );
        }
        return null;
    };

    // for on-market listing
    const renderWithdrawnLabel = (isOwner, isActive, isSold, marketStatus, mLSGroupType) => {
        if (isOwner && !isActive && !isSold && !mLSGroupType) {
            return (
                <div className="re-post-label temporarily-withdrawn-label" data-testid="withdrawnlabel">
                    {resx(RESX_LISTINGS, 'LISTING_WITHDRAWN_TEXT')}
                </div>
            );
        }
        return null;
    };

    const renderActiveLabel = (isOwner, isActive, isSold, marketStatus) => {
        if (isOwner && isActive && marketStatus == 0 && !isSold) {
            return (
                <div className="re-post-label listing-active" data-testid="activelabel">
                    {resx(RESX_LISTINGS, 'LISTING_ACTIVE_TEXT')}
                </div>
            );
        }
        return null;
    };

    const renderSoldLabel = (isOwner, listingTransactionType, isSold, isActive) => {
        let soldLabelText;

        // if TRANSACTION TYPE of listing is Lease (ListingTransactionType: 1), show "Leased" text
        if (isSold && isActive) {
            if (listingTransactionType === 1) {
                soldLabelText = resx(RESX_LISTINGS, 'LISTING_LEASED_TEXT');
            } else if (listingTransactionType === 0) {
                soldLabelText = resx(RESX_LISTINGS, 'LISTING_SOLS_TEXT');
            }

            return (
                <span className="re-post-label listing-sold" data-testid="soldlabel">
                    <span>{soldLabelText}</span>
                </span>
            );
        }
        return null;
    };

    const renderHiddenAddressLabel = (isOwner, hideAddress) => {
        //if owner view their own public listing or owner is in my listings page.
        const showLabel = _path(['RESAAS', 'User2', 'WebsiteId'], window) === window.targetUserWebsiteId || isOwner;

        if (showLabel && hideAddress) {
            return (
                <div className="re-post-label hidden-address-label">{resx(RESX_LISTINGS, 'HIDDEN_ADDRESS_TEXT')}</div>
            );
        }
        return null;
    };

    const renderListingAutoWithdrawnLabel = (
        isOwner,
        marketStatus,
        isSold,
        isActive,
        withdrawnRemainingDays,
        MLSGroupType
    ) => {
        let htmlText;

        if (
            isOwner &&
            !isSold &&
            isActive &&
            !MLSGroupType &&
            !gateAutoExpireListingsFeature &&
            withdrawnRemainingDays >= 0
        ) {
            const withdrawnSoon =
                (marketStatus === 0 && withdrawnRemainingDays <= listingExpiryNotificationInDays) ||
                (marketStatus === 1 && withdrawnRemainingDays < 3);

            const prettyDays =
                marketStatus === 0 ? Math.ceil(withdrawnRemainingDays) : Math.floor(withdrawnRemainingDays);

            if (withdrawnRemainingDays < 1) {
                htmlText = resx(RESX_LISTINGS, 'LISTING_WITHDRAWN_AUTO_TODAY');
            } else if (prettyDays > 1) {
                htmlText = format(resx(RESX_LISTINGS, 'LISTING_WITHDRAWN_AUTO'), prettyDays);
            } else if (prettyDays === 1) {
                htmlText = format(resx(RESX_LISTINGS, 'LISTING_WITHDRAWN_AUTO_1_DAY'), prettyDays);
            }

            return (
                <div
                    className={`${
                        withdrawnSoon
                            ? 're-post-label automatically-withdrawn-label-alert'
                            : 're-post-label automatically-withdrawn-label'
                    } ${withdrawnSoon && marketStatus === 0 ? 'automatically-withdrawn-label-clickable' : ''}`}
                    ref={expiryLabelRef}
                    onClick={withdrawnSoon && marketStatus === 0 ? e => handleLabelOpenMenuClick(e) : null}
                    dangerouslySetInnerHTML={{
                        __html: htmlText
                    }}
                    data-testid="automaticallywithdrawnlabel"
                />
            );
        }
        return null;
    };

    const renderPushToZillowLabel = (isOwner, PushToZillow, IsActive, IsSold, MarketStatus) => {
        if (isOwner && PushToZillow && !IsSold && IsActive && MarketStatus === 1) {
            return (
                <div className="re-post-label pushed-to-zillow-label" data-testid="pushedtozillowlabel">
                    {resx(RESX_LISTINGS, 'PUSHED_TO_ZILLOW_LABEL_TEXT')}
                </div>
            );
        }
        return null;
    };

    const renderDraftLabel = (isOwner, IsLive) => {
        if (isOwner && !IsLive) {
            return (
                <div className="re-post-label draft-label" data-testid="draftlabel">
                    {resx(RESX_LISTINGS, 'DRAFT_LABEL_TEXT')}
                </div>
            );
        }
        return null;
    };

    const renderListingAutoWithdrawnPublicLabel = (isOwner, IsSold, IsActive, withdrawnRemainingDays, MLSGroupType) => {
        // public view: e.g. Listed on MLS in 3 days

        if (!isOwner && !IsSold && IsActive && !MLSGroupType && withdrawnRemainingDays >= 0) {
            let htmlText;
            const prettyDays = Math.floor(withdrawnRemainingDays);

            if (withdrawnRemainingDays < 1) {
                htmlText = resx(RESX_LISTINGS, 'PRE_MARKET_TODAY_TO_EXPIRY_TEXT');
            } else if (prettyDays > 1) {
                htmlText = format(resx(RESX_LISTINGS, 'PRE_MARKET_DAYS_TO_EXPIRY_TEXT'), prettyDays);
            } else if (prettyDays === 1) {
                htmlText = resx(RESX_LISTINGS, 'PRE_MARKET_1_DAY_TO_EXPIRY_TEXT');
            }
            return (
                <div
                    className="re-post-label automatically-withdrawn-label"
                    dangerouslySetInnerHTML={{ __html: htmlText }}
                    data-testid="publicwithdrawnlabel"
                />
            );
        }
        return null;
    };

    return (
        <div className="re-listing-labels">
            {isOwner && !IsLive ? (
                <div className="re-listing-labels-left">{renderDraftLabel(isOwner, IsLive)}</div>
            ) : (
                <React.Fragment>
                    <div className="re-listing-labels-left">
                        {renderActiveLabel(isOwner, IsActive, IsSold, MarketStatus)}
                        {renderSoldLabel(isOwner, ListingTransactionType, IsSold, IsActive)}
                        {renderOffmarketLabel(MarketStatus)}
                        {renderPremarketLabel(MarketStatus, MLSGroupType, IsSold, isOwner)}
                        {renderPushToZillowLabel(isOwner, PushToZillow, IsActive, IsSold, MarketStatus)}
                        {renderPushedToMlsLabel(isOwner, ListingPushedToMLS, MLSGroupType)}
                        {renderWithdrawnTimeLabel(isOwner, IsSold, IsActive, MLSPremarketIntervalHours)}
                        {renderWithdrawnLabel(isOwner, IsActive, IsSold, MarketStatus, MLSGroupType)}
                        {renderTemporarilyWithdrawnLabel(isOwner, IsActive, MarketStatus, MLSGroupType)}
                        {renderHiddenAddressLabel(isOwner, HideAddress)}
                    </div>
                    <div className="re-listing-labels-right" data-testid="labelsright">
                        {renderListingAutoWithdrawnLabel(
                            isOwner,
                            MarketStatus,
                            IsSold,
                            IsActive,
                            WithdrawnRemainingDays,
                            MLSGroupType
                        )}
                        {renderListingAutoWithdrawnPublicLabel(
                            isOwner,
                            IsSold,
                            IsActive,
                            WithdrawnRemainingDays,
                            MLSGroupType

                        )}
                    </div>
                </React.Fragment>
            )}
        </div>
    );
};

ListingLabels.propTypes = {
    listing: PropTypes.object,
    isOwner: PropTypes.bool,
    callBack: PropTypes.func
};

export default ListingLabels;
